import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Permission, Role } from '@foundation/shared';
import { can } from '@foundation/shared';
import { api, setUnauthenticatedHandler, tokenStore } from './lib/api';

export interface Me {
  id: string;
  staffCode: string;
  fullName: string;
  nickname: string | null;
  email: string | null;
  phone: string | null;
  role: Role;
  teamId: string | null;
  teamName: string | null;
  areaIds: string[];
  lineLinked: boolean;
  lastLoginAt: string | null;
  via: 'liff' | 'admin';
}

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** เริ่มขั้นตอนล็อกอิน — พาไปหน้าของ Google/LINE (ฟังก์ชันนี้ไม่คืนค่า) */
  startLogin: (provider: 'google' | 'line') => Promise<void>;
  /** แลกโค้ดที่ callback ส่งกลับมาเป็น session */
  completeLogin: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  allowed: (permission: Permission) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // token หมดอายุระหว่างใช้งาน → เคลียร์ state ให้ router พาไปหน้า login
  useEffect(() => {
    setUnauthenticatedHandler(() => setMe(null));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tokenStore.get()) {
        setLoading(false);
        return;
      }
      try {
        const profile = await api<Me>('/auth/me');
        if (!cancelled) setMe(profile);
      } catch {
        tokenStore.clear();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const startLogin = useCallback(async (provider: 'google' | 'line') => {
    const res = await api<{ authorizeUrl: string }>(`/auth/sso/${provider}/start`, {
      method: 'POST',
    });
    window.location.href = res.authorizeUrl;
  }, []);

  const completeLogin = useCallback(async (code: string) => {
    const res = await api<{ token: string }>('/auth/sso/exchange', {
      method: 'POST',
      body: { code },
    });
    tokenStore.set(res.token);
    setMe(await api<Me>('/auth/me'));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<void>('/auth/logout', { method: 'POST' });
    } finally {
      tokenStore.clear();
      setMe(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      startLogin,
      completeLogin,
      logout,
      // ใช้ซ่อน/แสดงเมนูเท่านั้น — การบังคับสิทธิ์จริงอยู่ที่ Worker
      allowed: (permission: Permission) => (me ? can(me.role, permission) : false),
    }),
    [me, loading, startLogin, completeLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth ต้องอยู่ภายใน AuthProvider');
  return ctx;
}
